import React, { useState, useCallback, useMemo, memo } from "react";
import { Check, X, Loader2, AlertCircle } from "lucide-react";
import { FormField } from "../services/FormServices";

// Verification Status Types
export type VerificationStatus = "unverified" | "verifying" | "verified" | "failed";

// Verification Configuration
interface VerificationConfig {
  verifyFunction?: (value: any, field: FormField) => Promise<boolean>;
  showButton?: boolean;
  showStatus?: boolean;
  allowRetry?: boolean;
  autoVerify?: boolean;
  debounceMs?: number;
}

// Field Verification Props
interface FieldVerificationProps {
  field: FormField;
  value: any;
  status: VerificationStatus;
  config?: VerificationConfig;
  onStatusChange: (status: VerificationStatus) => void;
  className?: string;
  disabled?: boolean;
}

// Verification Button Component
interface VerificationButtonProps {
  status: VerificationStatus;
  value: any;
  field: FormField;
  onVerify: () => void;
  disabled?: boolean;
  className?: string;
}

const VerificationButton = memo<VerificationButtonProps>(({
  status,
  value,
  field,
  onVerify,
  disabled = false,
  className = ""
}) => {
  const buttonConfig = useMemo(() => {
    const hasValue = value !== undefined && value !== null && value !== "";
    
    switch (status) {
      case "verified":
        return {
          text: "Verified",
          icon: <Check className="w-3 h-3" />,
          className: "bg-green-100 text-green-700 border border-green-200",
          disabled: true
        };
      case "failed":
        return {
          text: "Retry",
          icon: <X className="w-3 h-3" />,
          className: "bg-red-100 text-red-700 border border-red-200 hover:bg-red-200",
          disabled: !hasValue
        };
      case "verifying":
        return {
          text: "Verifying...",
          icon: <Loader2 className="w-3 h-3 animate-spin" />,
          className: "bg-blue-100 text-blue-700 border border-blue-200",
          disabled: true
        };
      default:
        return {
          text: "Verify",
          icon: null,
          className: "bg-blue-600 text-white hover:bg-blue-700",
          disabled: !hasValue
        };
    }
  }, [status, value]);

  const isDisabled = disabled || buttonConfig.disabled;

  const handleClick = useCallback(() => {
    if (!isDisabled) {
      onVerify();
    }
  }, [isDisabled, onVerify]);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      className={`
        h-10 px-3 py-2 text-sm transition-all duration-200 
        flex items-center gap-1 rounded-r
        ${buttonConfig.className}
        ${isDisabled ? "cursor-not-allowed opacity-50" : ""}
        ${className}
      `}
    >
      {buttonConfig.icon}
      {buttonConfig.text}
    </button>
  );
});

VerificationButton.displayName = "VerificationButton";

// Verification Status Indicator
interface VerificationStatusProps {
  status: VerificationStatus;
  message?: string;
  className?: string;
}

const VerificationStatus = memo<VerificationStatusProps>(({
  status,
  message,
  className = ""
}) => {
  const statusConfig = useMemo(() => {
    switch (status) {
      case "verified":
        return {
          icon: <Check className="w-4 h-4" />,
          className: "text-green-600 bg-green-50 border-green-200",
          defaultMessage: "Verification successful"
        };
      case "failed":
        return {
          icon: <X className="w-4 h-4" />,
          className: "text-red-600 bg-red-50 border-red-200",
          defaultMessage: "Verification failed"
        };
      case "verifying":
        return {
          icon: <Loader2 className="w-4 h-4 animate-spin" />,
          className: "text-blue-600 bg-blue-50 border-blue-200",
          defaultMessage: "Verifying..."
        };
      default:
        return null;
    }
  }, [status]);

  if (!statusConfig || status === "unverified") {
    return null;
  }

  return (
    <div className={`
      flex items-center gap-2 px-2 py-1 text-xs border rounded-md
      ${statusConfig.className}
      ${className}
    `}>
      {statusConfig.icon}
      <span>{message || statusConfig.defaultMessage}</span>
    </div>
  );
});

VerificationStatus.displayName = "VerificationStatus";

// Main Field Verification Component
export const FieldVerification = memo<FieldVerificationProps>(({
  field,
  value,
  status,
  config = {},
  onStatusChange,
  className = "",
  disabled = false
}) => {
  const [lastVerifiedValue, setLastVerifiedValue] = useState<any>(null);
  const [verificationMessage, setVerificationMessage] = useState<string>("");

  const {
    verifyFunction,
    showButton = true,
    showStatus = true,
    allowRetry = true,
    autoVerify = false,
    debounceMs = 1000
  } = config;

  // Default verification function if none provided
  const defaultVerifyFunction = useCallback(async (val: any, fld: FormField) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Basic validation examples
    if (fld.type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(val);
    }
    
    if (fld.type === "phone") {
      const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;
      return phoneRegex.test(val);
    }
    
    // Default: just check if value exists and meets minimum requirements
    return val && val.toString().length >= (fld.minLength || 1);
  }, []);

  const performVerification = useCallback(async () => {
    if (!value || status === "verifying") return;

    try {
      onStatusChange("verifying");
      setVerificationMessage("");

      const verifyFn = verifyFunction || defaultVerifyFunction;
      const isValid = await verifyFn(value, field);

      if (isValid) {
        onStatusChange("verified");
        setLastVerifiedValue(value);
        setVerificationMessage("Verification successful");
      } else {
        onStatusChange("failed");
        setVerificationMessage("Verification failed - please check your input");
      }
    } catch (error) {
      onStatusChange("failed");
      setVerificationMessage("Verification error - please try again");
      console.error("Verification error:", error);
    }
  }, [value, status, verifyFunction, defaultVerifyFunction, field, onStatusChange]);

  // Auto-verify with debounce
  React.useEffect(() => {
    if (!autoVerify || !value || status === "verifying") return;

    // Don't auto-verify if already verified with same value
    if (status === "verified" && lastVerifiedValue === value) return;

    const timeoutId = setTimeout(() => {
      performVerification();
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [value, autoVerify, performVerification, debounceMs, status, lastVerifiedValue]);

  // Reset status when value changes significantly
  React.useEffect(() => {
    if (status === "verified" && lastVerifiedValue !== value && value) {
      onStatusChange("unverified");
      setVerificationMessage("");
    }
  }, [value, lastVerifiedValue, status, onStatusChange]);

  const handleVerify = useCallback(() => {
    if (status === "failed" && !allowRetry) return;
    performVerification();
  }, [status, allowRetry, performVerification]);

  // Don't render if verification is not enabled for this field
  if (!field.showVerifyButton && !showButton) {
    return null;
  }

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {/* Verification Button (inline with input) */}
      {showButton && (
        <VerificationButton
          status={status}
          value={value}
          field={field}
          onVerify={handleVerify}
          disabled={disabled}
        />
      )}

      {/* Verification Status Display */}
      {/* {showStatus && (
        <VerificationStatus
          status={status}
          message={verificationMessage}
        />
      )} */}
    </div>
  );
});

FieldVerification.displayName = "FieldVerification";

// Hook for managing verification states across form
export const useFormVerification = () => {
  const [verificationStates, setVerificationStates] = useState<
    Record<string, VerificationStatus>
  >({});

  const updateVerificationStatus = useCallback((
    fieldName: string,
    status: VerificationStatus
  ) => {
    setVerificationStates(prev => ({
      ...prev,
      [fieldName]: status
    }));
  }, []);

  const getVerificationStatus = useCallback((fieldName: string): VerificationStatus => {
    return verificationStates[fieldName] || "unverified";
  }, [verificationStates]);

  const resetVerificationStatus = useCallback((fieldName?: string) => {
    if (fieldName) {
      setVerificationStates(prev => {
        const newStates = { ...prev };
        delete newStates[fieldName];
        return newStates;
      });
    } else {
      setVerificationStates({});
    }
  }, []);

  const getAllVerificationStates = useCallback(() => {
    return verificationStates;
  }, [verificationStates]);

  const isAllVerified = useCallback((fieldNames: string[]) => {
    return fieldNames.every(name => 
      verificationStates[name] === "verified"
    );
  }, [verificationStates]);

  return {
    verificationStates,
    updateVerificationStatus,
    getVerificationStatus,
    resetVerificationStatus,
    getAllVerificationStates,
    isAllVerified
  };
};

// Verification Service - for complex verification logic
export class VerificationService {
  static async verifyEmail(email: string): Promise<boolean> {
    // Mock API call to verify email
    await new Promise(resolve => setTimeout(resolve, 1000));
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  static async verifyPhone(phone: string): Promise<boolean> {
    // Mock API call to verify phone
    await new Promise(resolve => setTimeout(resolve, 1200));
    const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;
    return phoneRegex.test(phone);
  }

  static async verifyUnique(value: string, field: string): Promise<boolean> {
    // Mock API call to check uniqueness
    await new Promise(resolve => setTimeout(resolve, 800));
    // Simulate some values already exist
    const existingValues = ['admin', 'user123', 'test@example.com'];
    return !existingValues.includes(value.toLowerCase());
  }

  static async verifyCustom(
    value: any,
    validationFunction: (val: any) => Promise<boolean>
  ): Promise<boolean> {
    try {
      return await validationFunction(value);
    } catch (error) {
      console.error("Custom verification error:", error);
      return false;
    }
  }
}